import { and, desc, eq, inArray, isNotNull, or } from 'drizzle-orm'
import { createTransport } from 'nodemailer'
import { inngest } from '@/lib/inngest'
import { db as defaultDb } from '@/db'
import { aiArtifacts, tenantConfigs, tenantMemberships, tenants, tickets, users } from '@/db/schema'
import { buildExpertDigestEmail, type DigestTicket } from '@/lib/expert-digest-email'
import type { PostgresJsDatabase } from 'drizzle-orm/postgres-js'
import type * as schema from '@/db/schema'

/** Ticket statuses that represent work awaiting expert action (first response). */
const PENDING_STATUSES = ['AWAITING_EXPERT', 'EXPERT_REVIEWING', 'RESPONSE_DRAFTING', 'RESPONSE_READY'] as const

type DB = PostgresJsDatabase<typeof schema>

export type DigestResult = {
  emailsSent: number
  skippedTenants: number
}

/** Pure business logic — separated from Inngest wrapper for testability. */
export async function runExpertDigest(options?: {
  db?: DB
  sendEmail?: (to: string, email: { subject: string; html: string; text: string }) => Promise<void>
}): Promise<DigestResult> {
  const db = (options?.db ?? defaultDb) as DB
  const sendEmail = options?.sendEmail ?? buildDefaultSender()

  const allTenants = await db
    .select({ id: tenants.id, expertDigestEnabled: tenantConfigs.expertDigestEnabled })
    .from(tenants)
    .leftJoin(tenantConfigs, eq(tenantConfigs.tenantId, tenants.id))

  let emailsSent = 0
  let skippedTenants = 0

  for (const tenant of allTenants) {
    if (tenant.expertDigestEnabled === false) {
      skippedTenants++
      continue
    }

    const experts = await db
      .select({ id: users.id, email: users.email, displayName: users.displayName })
      .from(users)
      .innerJoin(tenantMemberships, eq(tenantMemberships.userId, users.id))
      .where(and(
        eq(tenantMemberships.tenantId, tenant.id),
        inArray(tenantMemberships.role, ['expert', 'admin']),
        eq(tenantMemberships.isActive, true),
      ))

    if (experts.length === 0) {
      skippedTenants++
      continue
    }

    const pendingTickets = await db
      .select({
        id: tickets.id,
        referenceNumber: tickets.referenceNumber,
        title: tickets.title,
        status: tickets.status,
        createdAt: tickets.createdAt,
      })
      .from(tickets)
      .where(and(
        eq(tickets.tenantId, tenant.id),
        or(
          inArray(tickets.status, [...PENDING_STATUSES]),
          and(
            eq(tickets.status, 'AWAITING_REPLY'),
            isNotNull(tickets.resolutionSeverity),
          ),
        ),
      ))
      .orderBy(desc(tickets.createdAt))

    if (pendingTickets.length === 0) {
      skippedTenants++
      continue
    }

    const ticketIds = pendingTickets.map((t) => t.id)

    const summaryRows = await db
      .select({ ticketId: aiArtifacts.ticketId, content: aiArtifacts.content })
      .from(aiArtifacts)
      .where(and(
        inArray(aiArtifacts.ticketId, ticketIds),
        eq(aiArtifacts.artifactType, 'summary'),
      ))
      .orderBy(desc(aiArtifacts.createdAt))

    // Build map of ticketId -> latest summary (rows are ordered newest-first so first hit wins)
    const summaryMap = new Map<string, string>()
    for (const row of summaryRows) {
      if (!summaryMap.has(row.ticketId)) {
        summaryMap.set(row.ticketId, row.content)
      }
    }

    const digestTickets: DigestTicket[] = pendingTickets.map((t) => ({
      ...t,
      summary: summaryMap.get(t.id) ?? null,
    }))

    const email = buildExpertDigestEmail({ tickets: digestTickets })

    for (const expert of experts) {
      try {
        await sendEmail(expert.email, email)
        emailsSent++
      } catch (err) {
        console.error(`[expert-digest] Failed to send digest to ${expert.email}:`, err)
      }
    }
  }

  return { emailsSent, skippedTenants }
}

function buildDefaultSender() {
  if (!process.env.EMAIL_SERVER_HOST) {
    return async (_to: string, _email: { subject: string; html: string; text: string }) => {
      // No-op when email is not configured
    }
  }

  const transport = createTransport({
    host: process.env.EMAIL_SERVER_HOST,
    port: Number(process.env.EMAIL_SERVER_PORT ?? 587),
    auth: process.env.EMAIL_SERVER_USER
      ? { user: process.env.EMAIL_SERVER_USER, pass: process.env.EMAIL_SERVER_PASSWORD }
      : undefined,
  })

  return async (to: string, email: { subject: string; html: string; text: string }) => {
    await transport.sendMail({ to, from: process.env.EMAIL_FROM, ...email })
  }
}

export const expertDigestFn = inngest.createFunction(
  { id: 'expert-digest', triggers: [{ cron: '0 23 * * *' }] },
  async ({ step }) => {
    return await step.run('send-expert-digest', () => runExpertDigest())
  },
)
